import { useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Calendar as CalendarIcon,
  MapPin,
  Target,
  Trash2,
  Pencil,
  X,
} from "lucide-react";
import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  listEvents,
  createEvent,
  updateEvent,
  deleteEvent,
} from "@/api/events";
import type {
  Event,
  EventCreateIn,
  EventPriority,
  EventType,
} from "@/types";
import { cn } from "@/lib/utils";
import { fmtDate } from "@/lib/dates";

const EVENT_TYPES: Array<{ value: EventType; label: string }> = [
  { value: "70.3", label: "IRONMAN 70.3" },
  { value: "ironman", label: "IRONMAN" },
  { value: "sprint_tri", label: "Sprint Triathlon" },
  { value: "olympic_tri", label: "Olympic Triathlon" },
  { value: "marathon", label: "Marathon" },
  { value: "half_marathon", label: "Half Marathon" },
  { value: "10k", label: "10K Run" },
  { value: "5k", label: "5K Run" },
  { value: "cycling_race", label: "Cycling Race" },
  { value: "open_water_swim", label: "Open Water Swim" },
  { value: "other", label: "Other" },
];

const PRIORITY_META: Record<EventPriority, { labelKey: string; cls: string }> = {
  A: { labelKey: "events_section.priority_a_meta", cls: "bg-coral/15 text-coral-dark border-coral/30" },
  B: { labelKey: "events_section.priority_b_meta", cls: "bg-amber/15 text-amber-dark border-amber/30" },
  C: { labelKey: "events_section.priority_c_meta", cls: "bg-cream-border text-taupe border-cream-border" },
};

function parseHms(s: string): number | null {
  if (!s.trim()) return null;
  const parts = s.split(":").map((p) => Number(p));
  if (parts.some((p) => Number.isNaN(p))) return null;
  let [h, m, sec] = [0, 0, 0];
  if (parts.length === 3) [h, m, sec] = parts;
  else if (parts.length === 2) [m, sec] = parts;
  else return null;
  return h * 3600 + m * 60 + sec;
}

export function EventsSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const eventsQ = useQuery({
    queryKey: ["events"],
    queryFn: () => listEvents(false),
  });
  const [editing, setEditing] = useState<Event | null>(null);
  const [creating, setCreating] = useState(false);
  const refreshRaceDates = () => Promise.all(
    ["events", "profile", "dashboard", "plan", "week"].map(key => qc.invalidateQueries({ queryKey: [key] })),
  );

  const delM = useMutation({
    mutationFn: (id: number) => deleteEvent(id),
    onSuccess: refreshRaceDates,
  });

  const events = eventsQ.data ?? [];
  const sorted = useMemo(() => [...events].sort((a, b) => a.date.localeCompare(b.date)), [events]);

  return (
    <Card id="events">
      <div className="flex items-center justify-between">
        <SectionLabel>{t("events_section.section_label")}</SectionLabel>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1 rounded-md bg-navy px-3 py-1.5 text-[12px] font-medium text-cream-light hover:bg-navy-deep"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2} />
          {t("events_section.add_button")}
        </button>
      </div>

      {eventsQ.isLoading ? (
        <p className="mt-3 text-[13px] text-taupe">{t("events_section.loading")}</p>
      ) : sorted.length === 0 ? (
        <p className="mt-3 text-[13px] text-taupe">{t("events_section.empty_state")}</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {sorted.map((e) => (
            <EventRow
              key={e.id}
              event={e}
              onEdit={() => setEditing(e)}
              onDelete={() => {
                if (confirm(t("events_section.delete_confirm", { name: e.name }))) delM.mutate(e.id);
              }}
            />
          ))}
        </ul>
      )}

      {creating ? (
        <EventModal
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void refreshRaceDates();
          }}
        />
      ) : null}
      {editing ? (
        <EventModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refreshRaceDates();
          }}
        />
      ) : null}
    </Card>
  );
}

function EventRow({
  event,
  onEdit,
  onDelete,
}: {
  event: Event;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const meta = PRIORITY_META[event.priority];
  return (
    <li className="rounded-lg border border-cream-border bg-cream p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-1.5">
            <span className={cn("rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider", meta.cls)}>
              {event.priority}
            </span>
            <span className="text-[11px] text-taupe">{event.event_type_label}</span>
            {event.days_until >= 0 ? (
              <span className="text-[11px] text-amber-dark">
                · {t("events_section.in_n_days", { n: event.days_until })}
              </span>
            ) : (
              <span className="text-[11px] text-taupe">
                · {t("events_section.n_days_ago", { n: Math.abs(event.days_until) })}
              </span>
            )}
          </div>
          <div className="font-medium text-[14px] text-navy-deep">{event.name}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-taupe">
            <span className="inline-flex items-center gap-1">
              <CalendarIcon className="h-3 w-3" strokeWidth={1.8} />
              {fmtDate(event.date, "d MMMM yyyy")}
            </span>
            {event.location ? (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3 w-3" strokeWidth={1.8} />
                {event.location}
              </span>
            ) : null}
            {event.goal_time_formatted ? (
              <span className="inline-flex items-center gap-1">
                <Target className="h-3 w-3" strokeWidth={1.8} />
                {event.goal_time_formatted}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            onClick={onEdit}
            title={t("events_section.edit_tooltip")}
            className="rounded-md border border-cream-border bg-cream-light p-1.5 text-taupe hover:text-navy-deep"
          >
            <Pencil className="h-3.5 w-3.5" strokeWidth={1.8} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            title={t("events_section.delete_tooltip")}
            className="rounded-md border border-cream-border bg-cream-light p-1.5 text-coral-dark hover:bg-coral/10"
          >
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
          </button>
        </div>
      </div>
    </li>
  );
}

function EventModal({
  initial,
  onClose,
  onSaved,
}: {
  initial?: Event;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name ?? "");
  const [type, setType] = useState<EventType>(initial?.event_type ?? "70.3");
  const [priority, setPriority] = useState<EventPriority>(initial?.priority ?? "A");
  const [date, setDate] = useState(initial?.date ?? "");
  const [location, setLocation] = useState(initial?.location ?? "");
  const [goalTime, setGoalTime] = useState(initial?.goal_time_formatted ?? "");
  const [goalNotes, setGoalNotes] = useState(initial?.goal_notes ?? "");
  const [registered, setRegistered] = useState(initial?.registered ?? false);
  const [registrationUrl, setRegistrationUrl] = useState(initial?.registration_url ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (name.trim().length < 2) {
      setError(t("events_section.name_too_short"));
      return;
    }
    if (!date) {
      setError(t("events_section.specify_date"));
      return;
    }
    const goalSeconds = parseHms(goalTime);
    if (goalTime.trim() && goalSeconds === null) {
      setError(t("events_section.goal_time_format"));
      return;
    }
    const payload: EventCreateIn = {
      name: name.trim(),
      event_type: type,
      priority,
      date,
      location: location.trim() || null,
      goal_time_seconds: goalSeconds,
      goal_notes: goalNotes.trim() || null,
      registered,
      registration_url: registrationUrl.trim() || null,
    };
    setSaving(true);
    try {
      if (initial) {
        await updateEvent(initial.id, payload);
      } else {
        await createEvent(payload);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("events_section.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy-deep/60 p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl rounded-2xl border border-cream-border bg-cream-light p-5 shadow-soft"
      >
        <div className="mb-4 flex items-center justify-between">
          <div>
            <SectionLabel>{initial ? t("events_section.modal_edit") : t("events_section.modal_new")}</SectionLabel>
            <h3 className="mt-1 font-serif text-[20px] font-medium">
              {initial ? initial.name : t("events_section.modal_default_name")}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="text-taupe hover:text-navy-deep">
            <X className="h-4 w-4" strokeWidth={1.8} />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-taupe">{t("events_section.form_name")}</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="IRONMAN 70.3 Tallinn"
              required
              maxLength={200}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-taupe">{t("events_section.form_type")}</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as EventType)}
                className="mt-1 w-full rounded-md border border-cream-border bg-cream px-3 py-2 text-[14px]"
              >
                {EVENT_TYPES.map((et) => (
                  <option key={et.value} value={et.value}>
                    {et.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-taupe">{t("events_section.form_priority")}</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as EventPriority)}
                className="mt-1 w-full rounded-md border border-cream-border bg-cream px-3 py-2 text-[14px]"
              >
                <option value="A">{t("events_section.priority_a")}</option>
                <option value="B">{t("events_section.priority_b")}</option>
                <option value="C">{t("events_section.priority_c")}</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-taupe">{t("events_section.form_date")}</label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-taupe">{t("events_section.form_goal_time")}</label>
              <Input
                value={goalTime}
                onChange={(e) => setGoalTime(e.target.value)}
                placeholder="5:30:00"
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-taupe">{t("events_section.form_location")}</label>
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Tallinn, Estonia"
              maxLength={200}
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-taupe">{t("events_section.form_notes")}</label>
            <textarea
              value={goalNotes}
              onChange={(e) => setGoalNotes(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border border-cream-border bg-cream px-3 py-2 text-[14px] font-mono"
              placeholder={t("events_section.form_notes_placeholder")}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="registered"
              type="checkbox"
              checked={registered}
              onChange={(e) => setRegistered(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-cream-border"
            />
            <label htmlFor="registered" className="text-[13px] text-navy-deep">{t("events_section.form_registered")}</label>
            {registered ? (
              <Input
                value={registrationUrl}
                onChange={(e) => setRegistrationUrl(e.target.value)}
                placeholder="https://…"
                className="ml-2 flex-1"
                maxLength={500}
              />
            ) : null}
          </div>

          {error ? <p className="text-[13px] text-coral-dark">{error}</p> : null}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>{t("events_section.cancel")}</Button>
          <Button type="submit" disabled={saving}>
            {saving ? t("events_section.saving") : initial ? t("events_section.save") : t("events_section.add_button")}
          </Button>
        </div>
      </form>
    </div>
  );
}
